import Button from '@/shared/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import DialogHeader from '@/shared/DialogHeader'
import Typography from '@mui/material/Typography'
import {
  Box,
} from '@mui/material';
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import durationToTranslatedStr from '@/libs/utils/durationToTranslatedStr'
import moment from 'moment'
import { DATE_TIME_FORMAT } from '@/libs/core/constants'


const mapStateToProps = ({ loading }) => ({
  isLoading: loading['account/createAccount'] || loading['account/editAccount'],
  isRolesLoading: loading['account/getUserRoles'],
  isCompanyLoading: loading['account/getCompany'],
})


const TextItem = ({ label, value, suffix, sx, labelWidth = 80 }) => (
  <Grid container alignItems="flex-start" spacing={2} sx={sx}>
    {/* Label 左側固定寬度 */}
    <Grid item sx={{ width: labelWidth }}>
      <Typography
        sx={{
          fontWeight: 'bold',
          color: '#2D2D6C',
        }}
      >
        {label}
      </Typography>
    </Grid>

    {/* Value 佔滿剩餘空間，多行換行 + 黑色底線 + 灰色文字 */}
    <Grid item xs>
      <Box
        sx={{
          borderBottom: '1px solid #000', // 黑色底線
          width: '100%',
        }}
      >
        <Typography
          sx={{
            color: '#999999',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            paddingBottom: '2px',
            paddingLeft: '20px'
          }}
        >
          {value}
        </Typography>
      </Box>
    </Grid>

    {/* 可選的 suffix */}
    {suffix && (
      <Grid item>
        <Typography
          sx={{
            fontWeight: 'bold',
            color: '#2D2D6C',
          }}
        >
          {suffix}
        </Typography>
      </Grid>
    )}
  </Grid>
)


const TextItemCustom = ({ label, value, suffix, sx }) => (
  <Grid container direction="column" spacing={1} sx={sx}>
    {/* Label 在上方 */}
    <Grid item>
      <Typography
        sx={{
          fontWeight: 'bold',
          color: '#2D2D6C',
        }}
      >
        {label}
      </Typography>
    </Grid>

    {/* Value 在下方 */}
    <Grid item>
      <Box sx={{ width: '100%', pl: '10px' }}>
        {typeof value === 'string' || typeof value === 'number' ? (
          <Typography
            sx={{
              color: '#999999',          //  灰字
              whiteSpace: 'pre-wrap',    //  保留換行
              wordBreak: 'break-word',   //  長字自動換行
              lineBreak: 'strict',
              paddingBottom: '4px',
            }}
          >
            {value}
          </Typography>
        ) : (
          <Box
            sx={{
              color: '#999999',          //  灰字（套用到整個內容）
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineBreak: 'strict',
            }}
          >
            {value}
          </Box>
        )}
      </Box>
    </Grid>

    {suffix && (
      <Grid item>
        <Typography
          sx={{
            fontWeight: 'bold',
            color: '#2D2D6C',
          }}
        >
          {suffix}
        </Typography>
      </Grid>
    )}
  </Grid>
);



const ViewPhishCampignDialog = ({
  dispatch,
  isLoading,
  isRolesLoading,
  isCustodiansLoading,
  isCompanyLoading,
  onClose,
  onComplete,
  data,
  ...props
}) => {

  const { t } = useTranslation()

  const handleClose = () => {
    onClose?.()
  }

  const {
    campaignMainName,
    campaignName,
    scheduledStartTimeS,
    scheduledEndTimeS,
    scheduledStartTimeE,
    scheduledEndTimeE,
    status,
    runType,
  } = data || {}

  const dayStr = t('general.day')
  const hourStr = t('general.hour')
  const minuteStr = t('general.minute')

  const campaignDuration = moment.duration(moment(scheduledEndTimeE).diff(scheduledStartTimeS))

  const campaignDurationStr = durationToTranslatedStr(campaignDuration, {
    day: dayStr,
    hour: hourStr,
    minute: minuteStr,
  })

  const remainDuration = moment.duration(moment(scheduledEndTimeE).diff())

  const remainDurationStr = durationToTranslatedStr(
    moment.duration(
      Math.max(Math.min(campaignDuration.asSeconds(), remainDuration.asSeconds()), 0),
      'seconds',
    ),
    {
      day: dayStr,
      hour: hourStr,
      minute: minuteStr,
    },
  )

  const runTypeStr = str => {
    switch (str) {
      case 'main':
        return t('general.run_type_main')
      case 'pre':
        return t('general.run_type_pre')
      case 'test':
        return t('general.run_type_test')
      default:
        return ''
    }
  }


  return (
    <Dialog {...props} maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: 5, // ← 圓角在這裡才有效
          overflow: 'hidden', //  必加，不然 Header 會蓋掉圓角
          width: 500,
          maxWidth: '80%',
        },
      }}>

      <DialogHeader
        title={t('general.view_campaign_content')}
        onClose={handleClose}
      />

      <DialogContent sx={{ width: 500, pr: 1 }}>
        <Box sx={{ flexGrow: 1, pt: 4, pl: 0, pr: 2 }}>
          <Typography sx={{ color: '#2D2D6C', fontWeight: 'bold', mb: 2, mt: -4 }}>
            {t('general.campaign_content_section')}
          </Typography>
          <Box
            sx={{
              borderRadius: 1,
              border: '1px solid #2D2D6C',
              px: 2,
              py: 3,
            }}
          >
            <TextItem label={t('general.campaign_info')} value={campaignMainName ?? campaignName} sx={{ mb: 3 }} />
            <TextItem label={t('general.start')} value={
              moment(scheduledStartTimeS).format(DATE_TIME_FORMAT) +
              (scheduledStartTimeE
                ? ` ~ ${moment(scheduledStartTimeE).format(DATE_TIME_FORMAT)}`
                : '')
            } sx={{ mb: 3 }} />
            <TextItem label={t('general.remaining_days')} value={remainDurationStr} sx={{ mb: 3 }} />
            <TextItem label={t('general.end')} value={(scheduledEndTimeS
              ? `${moment(scheduledEndTimeS).format(DATE_TIME_FORMAT)} ~ `
              : '') + moment(scheduledEndTimeE).format(DATE_TIME_FORMAT)}
              sx={{ mb: 3 }} />
            <TextItem label={t('general.campaign_days')} value={campaignDurationStr} sx={{ mb: 3 }} />
            <TextItem label={t('general.campaign_status')} value={t(`status.${status}`)} sx={{ mb: 3 }} />
            <TextItem label={t('general.campaign_run_type')} value={runTypeStr(runType)} sx={{ mb: 3 }} />
            <TextItemCustom
              label={t(`${t('general.group_head_count')}, ${t('general.campaign_content')}`)}
              value={
                <Stack>
                  {data?.campaignContent?.length > 0 ? (
                    data.campaignContent.map((c, index) => {
                      const { people, content, group } = c;
                      return (
                        <Stack
                          key={`content_${index + 1}`}
                          direction="column" // 改成垂直排列
                          alignItems="flex-start"
                          spacing={0.5} // 控制群組/人數 與內容的間距
                        >
                          <Typography>
                            {group} / {t('general.head_count', { num: people })} ,
                          </Typography>
                          <Typography>{content}</Typography>
                        </Stack>
                      );
                    })) : (
                    <Typography color="text.secondary">{t('general.no_data')}</Typography>
                  )}
                </Stack>
              }
              sx={{ mb: 3 }}
            />

          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          justifyContent: 'flex-end', //  右下角
        }}>
        <Button
          style={{ minWidth: 100, backgroundColor: '#2D2D6C' }}
          onClick={async () => {
            handleClose()
          }}
        >
          {t('general.close')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default connect(mapStateToProps)(ViewPhishCampignDialog)